// src/components/Form.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";

import { MathJax, MathJaxContext } from "better-react-mathjax";

const emptyVar = () => ({
  name: "",
  code: "",
  description: "",
  unit: "",
  type: "number",
});

const emptyItem = () => ({
  name: "",
  type: "text", // text | equation | function
  description: "",
  equation: "",
  language: "javascript",
  code: "",
  variables: [],
});

const emptySection = () => ({
  name: "",
  description: "",
  variables: [],
  items: [],
});

const exampleJson = () => ({
  name: "Ohm's Law",
  description: "Quick reference for basic electrical relationships.",
  version: 1,
  variables: [
    { name: "Voltage", code: "V", unit: "V", type: "number", description: "Potential difference" },
    { name: "Current", code: "I", unit: "A", type: "number", description: "Current through a load" },
    { name: "Resistance", code: "R", unit: "Ω", type: "number", description: "Opposition to current" },
  ],
  sections: [
    {
      name: "Basics",
      description: "Core relationships.",
      variables: [{ name: "Power", code: "P", unit: "W", type: "number", description: "Power dissipated" }],
      items: [
        {
          name: "Ohm's law",
          type: "equation",
          description: "Relationship between voltage, current, resistance.",
          equation: "V = I \\cdot R",
          variables: [{ name: "Voltage", code: "V" }, { name: "Current", code: "I" }, { name: "Resistance", code: "R" }],
        },
        {
          name: "Compute current",
          type: "function",
          description: "Compute current from voltage and resistance.",
          language: "javascript",
          code: "function current(V, R) {\n  return V / R;\n}",
          variables: [{ name: "Voltage", code: "V" }, { name: "Resistance", code: "R" }],
        },
        {
          name: "Notes",
          type: "text",
          description: "Units matter. Use consistent units.",
          variables: [],
        },
      ],
    },
  ],
});

const normaliseIncoming = (data) => {
  const d = data || {};
  const vNum = Number.isFinite(d.version) ? d.version : parseInt(d.version || "1", 10);
  return {
    name: d.name || "",
    description: d.description || "",
    version: Number.isFinite(vNum) ? vNum : 1,
    variables: Array.isArray(d.variables) ? d.variables : [],
    sections: Array.isArray(d.sections) ? d.sections : [],
  };
};

function moveItem(arr, from, to) {
  const a = [...arr];
  if (from < 0 || from >= a.length) return a;
  if (to < 0 || to >= a.length) return a;
  const [x] = a.splice(from, 1);
  a.splice(to, 0, x);
  return a;
}

function downloadJson(obj, filename = "sheet.example.json") {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Form({ initialData, onSave, saving = false, error = "" }) {
  const initial = useMemo(() => normaliseIncoming(initialData), [initialData]);

  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);

  const [globalVars, setGlobalVars] = useState(initial.variables || []);
  const [sections, setSections] = useState(initial.sections || []);

  const [localError, setLocalError] = useState("");

  // JSON upload
  const fileRef = useRef(null);

  // Dialogs
  // NOTE: varDlg is now ONLY for GLOBAL vars (section vars moved into section dialog)
  const [varDlg, setVarDlg] = useState({ open: false, idx: -1, draft: emptyVar() });
  const [sectionDlg, setSectionDlg] = useState({ open: false, idx: -1, draft: emptySection() });
  const [itemDlg, setItemDlg] = useState({ open: false, sectionIdx: -1, idx: -1, draft: emptyItem() });

  // Menus
  const [menu, setMenu] = useState({ anchorEl: null, kind: "", sectionIdx: -1, idx: -1 });
  const openMenu = (e, kind, sectionIdx, idx) => setMenu({ anchorEl: e.currentTarget, kind, sectionIdx, idx });
  const closeMenu = () => setMenu({ anchorEl: null, kind: "", sectionIdx: -1, idx: -1 });

  useEffect(() => {
    setName(initial.name);
    setDescription(initial.description);
    setGlobalVars(initial.variables || []);
    setSections(initial.sections || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const validate = () => {
    if (!name.trim()) return "Sheet name is required.";
    return "";
  };

  const buildPayload = () => ({
    name: name.trim(),
    description: description.trim(),
    version: initialData?.version || 1,
    variables: (globalVars || []).map((v) => ({
      name: (v.name || "").trim(),
      code: (v.code || "").trim(),
      description: (v.description || "").trim(),
      unit: (v.unit || "").trim(),
      type: (v.type || "number").trim(),
    })),
    sections: (sections || []).map((s) => ({
      name: (s.name || "").trim(),
      description: (s.description || "").trim(),
      variables: (s.variables || []).map((v) => ({
        name: (v.name || "").trim(),
        code: (v.code || "").trim(),
        description: (v.description || "").trim(),
        unit: (v.unit || "").trim(),
        type: (v.type || "number").trim(),
      })),
      items: (s.items || []).map((it) => ({
        name: (it.name || "").trim(),
        type: (it.type || "text").trim(),
        description: (it.description || "").trim(),
        equation: (it.equation || "").trim(),
        language: (it.language || "javascript").trim(),
        code: it.code || "",
        variables: (it.variables || []).map((v) => ({
          name: (v.name || "").trim(),
          code: (v.code || "").trim(),
          description: (v.description || "").trim(),
          unit: (v.unit || "").trim(),
          type: (v.type || "number").trim(),
        })),
      })),
    })),
  });

  const handleSave = async () => {
    setLocalError("");
    const v = validate();
    if (v) return setLocalError(v);
    await onSave(buildPayload());
  };

  // JSON upload handler
  const handlePickJson = () => fileRef.current?.click();

  const handleJsonFile = async (file) => {
    setLocalError("");
    try {
      const txt = await file.text();
      const obj = JSON.parse(txt);
      const n = normaliseIncoming(obj);

      setName(n.name);
      setDescription(n.description);
      setGlobalVars(n.variables || []);
      setSections(n.sections || []);
    } catch (e) {
      setLocalError(`Invalid JSON: ${e?.message || String(e)}`);
    }
  };

  // helpers for lists
  const addGlobalVar = () => setVarDlg({ open: true, idx: -1, draft: emptyVar() });
  const editGlobalVar = (idx) => setVarDlg({ open: true, idx, draft: { ...globalVars[idx] } });

  const addSection = () => setSectionDlg({ open: true, idx: -1, draft: emptySection() });
  const editSection = (idx) => setSectionDlg({ open: true, idx, draft: { ...(sections[idx] || emptySection()) } });

  const addItem = (sectionIdx) => setItemDlg({ open: true, sectionIdx, idx: -1, draft: emptyItem() });
  const editItem = (sectionIdx, idx) =>
    setItemDlg({ open: true, sectionIdx, idx, draft: { ...(sections[sectionIdx]?.items?.[idx] || emptyItem()) } });

  // menu actions
  const removeSection = (idx) => setSections((prev) => prev.filter((_, i) => i !== idx));
  const moveSectionUp = (idx) => setSections((prev) => moveItem(prev, idx, idx - 1));
  const moveSectionDown = (idx) => setSections((prev) => moveItem(prev, idx, idx + 1));

  const removeGlobalVar = (idx) => setGlobalVars((prev) => prev.filter((_, i) => i !== idx));
  const moveGlobalVarUp = (idx) => setGlobalVars((prev) => moveItem(prev, idx, idx - 1));
  const moveGlobalVarDown = (idx) => setGlobalVars((prev) => moveItem(prev, idx, idx + 1));

  const removeItemAt = (sectionIdx, idx) =>
    setSections((prev) => {
      const next = [...prev];
      const s = { ...next[sectionIdx] };
      s.items = (s.items || []).filter((_, i) => i !== idx);
      next[sectionIdx] = s;
      return next;
    });

  const moveItemUpAt = (sectionIdx, idx) =>
    setSections((prev) => {
      const next = [...prev];
      const s = { ...next[sectionIdx] };
      s.items = moveItem(s.items || [], idx, idx - 1);
      next[sectionIdx] = s;
      return next;
    });

  const moveItemDownAt = (sectionIdx, idx) =>
    setSections((prev) => {
      const next = [...prev];
      const s = { ...next[sectionIdx] };
      s.items = moveItem(s.items || [], idx, idx + 1);
      next[sectionIdx] = s;
      return next;
    });

  // dialogs save handlers
  const saveVarDlg = () => {
    const d = { ...varDlg.draft };
    if (!d.name?.trim()) {
      setLocalError("Variable name is required.");
      return;
    }

    setGlobalVars((prev) => {
      const next = [...(prev || [])];
      if (varDlg.idx >= 0) next[varDlg.idx] = d;
      else next.push(d);
      return next;
    });

    setVarDlg({ open: false, idx: -1, draft: emptyVar() });
  };

  const saveSectionDlg = () => {
    const d = { ...sectionDlg.draft };
    if (!d.name?.trim()) {
      setLocalError("Section name is required.");
      return;
    }
    setSections((prev) => {
      const next = [...(prev || [])];
      if (sectionDlg.idx >= 0) next[sectionDlg.idx] = { ...next[sectionDlg.idx], ...d };
      else next.push(d);
      return next;
    });
    setSectionDlg({ open: false, idx: -1, draft: emptySection() });
  };

  const saveItemDlg = () => {
    const d = { ...itemDlg.draft };
    if (!d.name?.trim()) {
      setLocalError("Item name is required.");
      return;
    }
    if (!d.type?.trim()) {
      setLocalError("Item type is required.");
      return;
    }
    if (d.type === "equation" && !d.equation?.trim()) {
      setLocalError("Equation is required for equation items.");
      return;
    }
    if (d.type === "function" && !d.code?.trim()) {
      setLocalError("Code is required for function items.");
      return;
    }

    setSections((prev) => {
      const next = [...(prev || [])];
      const s = { ...(next[itemDlg.sectionIdx] || emptySection()) };
      const items = [...(s.items || [])];
      if (itemDlg.idx >= 0) items[itemDlg.idx] = d;
      else items.push(d);
      s.items = items;
      next[itemDlg.sectionIdx] = s;
      return next;
    });

    setItemDlg({ open: false, sectionIdx: -1, idx: -1, draft: emptyItem() });
  };

  const mathjaxConfig = useMemo(
    () => ({
      loader: { load: ["input/tex", "output/chtml"] },
      tex: { inlineMath: [["$", "$"], ["\\(", "\\)"]] },
    }),
    []
  );

  const renderItemPreview = (it) => {
    if (it.type === "equation") {
      const eq = it.equation || "";
      return (
        <MathJaxContext config={mathjaxConfig}>
          <Paper variant="outlined" sx={{ p: 1, bgcolor: "background.default" }}>
            <MathJax>{"\\(" + eq + "\\)"}</MathJax>
          </Paper>
        </MathJaxContext>
      );
    }
    if (it.type === "function") {
      return (
        <Paper variant="outlined" sx={{ p: 1, bgcolor: "background.default" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip size="small" label={it.language || "code"} />
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 13,
            }}
          >
            {it.code || ""}
          </Box>
        </Paper>
      );
    }
    return it.description ? (
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        {it.description}
      </Typography>
    ) : (
      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        No preview.
      </Typography>
    );
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6">Edit sheet</Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <input
                ref={fileRef}
                type="file"
                accept="application/json"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleJsonFile(f);
                  e.target.value = "";
                }}
              />

              <Tooltip title="Upload JSON to fill the form">
                <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={handlePickJson}>
                  Upload JSON
                </Button>
              </Tooltip>

              <Tooltip title="Download example JSON">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadJson(exampleJson(), "sheet.example.json")}
                >
                  Example JSON
                </Button>
              </Tooltip>

              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Stack>
          </Stack>

          {(error || localError) && <Alert severity="error">{error || localError}</Alert>}


          {/* Global variables */}
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Global variables</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addGlobalVar}>
                Add
              </Button>
            </Stack>

            {(globalVars || []).length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No global variables.
              </Typography>
            ) : (
              <List dense>
                {globalVars.map((v, idx) => (
                  <ListItem key={idx} divider>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontWeight: 600 }}>{v.name || "(unnamed)"}</Typography>
                          {v.code ? <Chip size="small" label={v.code} /> : null}
                          {v.unit ? <Chip size="small" variant="outlined" label={v.unit} /> : null}
                        </Stack>
                      }
                      secondary={v.description || ""}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => editGlobalVar(idx)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton onClick={(e) => openMenu(e, "globalVar", -1, idx)}>
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>

          <Divider />

          {/* Sections */}
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Sections</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}>
                Add section
              </Button>
            </Stack>

            {(sections || []).length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No sections.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {sections.map((s, sIdx) => (
                  <Paper key={sIdx} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 700 }}>{s.name || "(unnamed section)"}</Typography>
                            {(s.variables || []).length ? (
                              <Chip size="small" variant="outlined" label={`${(s.variables || []).length} vars`} />
                            ) : null}
                          </Stack>
                          {s.description ? (
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                              {s.description}
                            </Typography>
                          ) : null}
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Tooltip title="Edit section">
                            <IconButton onClick={() => editSection(sIdx)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton onClick={(e) => openMenu(e, "section", sIdx, -1)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Divider />

                      {/* Items */}
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2">Items</Typography>
                          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addItem(sIdx)}>
                            Add item
                          </Button>
                        </Stack>

                        {(s.items || []).length === 0 ? (
                          <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            No items.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {(s.items || []).map((it, idx) => (
                              <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                  <Stack spacing={0.5} sx={{ pr: 2, flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography sx={{ fontWeight: 650 }}>{it.name || "(unnamed item)"}</Typography>
                                      <Chip size="small" label={it.type || "text"} />
                                    </Stack>
                                    {it.description ? (
                                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                        {it.description}
                                      </Typography>
                                    ) : null}

                                    <Box sx={{ mt: 1 }}>{renderItemPreview(it)}</Box>

                                    {(it.variables || []).length ? (
                                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                                        {(it.variables || []).slice(0, 8).map((v, vi) => (
                                          <Chip
                                            key={vi}
                                            size="small"
                                            variant="outlined"
                                            label={v.code ? `${v.name} (${v.code})` : v.name}
                                            sx={{ mb: 0.5 }}
                                          />
                                        ))}
                                        {(it.variables || []).length > 8 ? (
                                          <Chip size="small" label={`+${(it.variables || []).length - 8} more`} />
                                        ) : null}
                                      </Stack>
                                    ) : null}
                                  </Stack>

                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Tooltip title="Edit item">
                                      <IconButton onClick={() => editItem(sIdx, idx)}>
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton onClick={(e) => openMenu(e, "item", sIdx, idx)}>
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Context menu (sectionVar removed) */}
      <Menu
        anchorEl={menu.anchorEl}
        open={!!menu.anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            const { kind, sectionIdx, idx } = menu;
            closeMenu();
            if (kind === "globalVar") editGlobalVar(idx);
            if (kind === "section") editSection(sectionIdx);
            if (kind === "item") editItem(sectionIdx, idx);
          }}
        >
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            const { kind, sectionIdx, idx } = menu;
            closeMenu();
            if (kind === "globalVar") setGlobalVars((p) => moveItem(p, idx, idx - 1));
            if (kind === "section") moveSectionUp(sectionIdx);
            if (kind === "item") moveItemUpAt(sectionIdx, idx);
          }}
        >
          Move up
        </MenuItem>

        <MenuItem
          onClick={() => {
            const { kind, sectionIdx, idx } = menu;
            closeMenu();
            if (kind === "globalVar") setGlobalVars((p) => moveItem(p, idx, idx + 1));
            if (kind === "section") moveSectionDown(sectionIdx);
            if (kind === "item") moveItemDownAt(sectionIdx, idx);
          }}
        >
          Move down
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            const { kind, sectionIdx, idx } = menu;
            closeMenu();
            if (kind === "globalVar") removeGlobalVar(idx);
            if (kind === "section") removeSection(sectionIdx);
            if (kind === "item") removeItemAt(sectionIdx, idx);
          }}
          sx={{ color: "error.main" }}
        >
          Remove
        </MenuItem>
      </Menu>

      {/* Variable dialog (GLOBAL only) */}
      <Dialog
        open={varDlg.open}
        onClose={() => setVarDlg({ open: false, idx: -1, draft: emptyVar() })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{varDlg.idx >= 0 ? "Edit variable" : "Add variable"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={varDlg.draft.name}
              onChange={(e) => setVarDlg((p) => ({ ...p, draft: { ...p.draft, name: e.target.value } }))}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Code (optional)"
                value={varDlg.draft.code}
                onChange={(e) => setVarDlg((p) => ({ ...p, draft: { ...p.draft, code: e.target.value } }))}
                fullWidth
              />
              <TextField
                label="Unit (optional)"
                value={varDlg.draft.unit}
                onChange={(e) => setVarDlg((p) => ({ ...p, draft: { ...p.draft, unit: e.target.value } }))}
                fullWidth
              />
              <TextField
                label="Type"
                value={varDlg.draft.type}
                onChange={(e) => setVarDlg((p) => ({ ...p, draft: { ...p.draft, type: e.target.value } }))}
                fullWidth
              />
            </Stack>
            <TextField
              label="Description (optional)"
              value={varDlg.draft.description}
              onChange={(e) => setVarDlg((p) => ({ ...p, draft: { ...p.draft, description: e.target.value } }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVarDlg({ open: false, idx: -1, draft: emptyVar() })}>Cancel</Button>
          <Button variant="contained" onClick={saveVarDlg}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section dialog (NOW includes section variables) */}
      <Dialog
        open={sectionDlg.open}
        onClose={() => setSectionDlg({ open: false, idx: -1, draft: emptySection() })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{sectionDlg.idx >= 0 ? "Edit section" : "Add section"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Header"
              value={sectionDlg.draft.name}
              onChange={(e) => setSectionDlg((p) => ({ ...p, draft: { ...p.draft, name: e.target.value } }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={sectionDlg.draft.description}
              onChange={(e) => setSectionDlg((p) => ({ ...p, draft: { ...p.draft, description: e.target.value } }))}
              fullWidth
              multiline
              minRows={3}
            />

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Section variables</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  setSectionDlg((p) => ({
                    ...p,
                    draft: { ...p.draft, variables: [...(p.draft.variables || []), emptyVar()] },
                  }))
                }
              >
                Add
              </Button>
            </Stack>

            {(sectionDlg.draft.variables || []).length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No section variables.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {(sectionDlg.draft.variables || []).map((v, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <TextField
                          label="Name"
                          value={v.name || ""}
                          onChange={(e) =>
                            setSectionDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), name: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          label="Code"
                          value={v.code || ""}
                          onChange={(e) =>
                            setSectionDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), code: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          label="Unit"
                          value={v.unit || ""}
                          onChange={(e) =>
                            setSectionDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), unit: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          select
                          label="Type"
                          value={v.type || "number"}
                          onChange={(e) =>
                            setSectionDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), type: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        >
                          <MenuItem value="number">number</MenuItem>
                          <MenuItem value="string">string</MenuItem>
                          <MenuItem value="boolean">boolean</MenuItem>
                        </TextField>
                      </Stack>

                      <TextField
                        label="Description"
                        value={v.description || ""}
                        onChange={(e) =>
                          setSectionDlg((p) => {
                            const vars = [...(p.draft.variables || [])];
                            vars[idx] = { ...(vars[idx] || emptyVar()), description: e.target.value };
                            return { ...p, draft: { ...p.draft, variables: vars } };
                          })
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />

                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button
                          onClick={() =>
                            setSectionDlg((p) => {
                              const vars = moveItem(p.draft.variables || [], idx, idx - 1);
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          disabled={idx === 0}
                        >
                          Up
                        </Button>
                        <Button
                          onClick={() =>
                            setSectionDlg((p) => {
                              const vars = moveItem(p.draft.variables || [], idx, idx + 1);
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          disabled={idx === (sectionDlg.draft.variables || []).length - 1}
                        >
                          Down
                        </Button>
                        <Button
                          color="error"
                          onClick={() =>
                            setSectionDlg((p) => {
                              const vars = (p.draft.variables || []).filter((_, i) => i !== idx);
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                        >
                          Remove
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDlg({ open: false, idx: -1, draft: emptySection() })}>Cancel</Button>
          <Button variant="contained" onClick={saveSectionDlg}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item dialog (Type is now a dropdown) */}
      <Dialog
        open={itemDlg.open}
        onClose={() => setItemDlg({ open: false, sectionIdx: -1, idx: -1, draft: emptyItem() })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{itemDlg.idx >= 0 ? "Edit item" : "Add item"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Name"
                value={itemDlg.draft.name}
                onChange={(e) => setItemDlg((p) => ({ ...p, draft: { ...p.draft, name: e.target.value } }))}
                fullWidth
              />

              <TextField
                select
                label="Type"
                value={itemDlg.draft.type || "text"}
                onChange={(e) => setItemDlg((p) => ({ ...p, draft: { ...p.draft, type: e.target.value } }))}
                fullWidth
              >
                <MenuItem value="text">text</MenuItem>
                <MenuItem value="equation">equation</MenuItem>
                <MenuItem value="function">function</MenuItem>
              </TextField>
            </Stack>

            <TextField
              label="Description (optional)"
              value={itemDlg.draft.description}
              onChange={(e) => setItemDlg((p) => ({ ...p, draft: { ...p.draft, description: e.target.value } }))}
              fullWidth
              multiline
              minRows={2}
            />

            {itemDlg.draft.type === "equation" ? (
              <>
                <TextField
                  label="Equation (LaTeX)"
                  value={itemDlg.draft.equation}
                  onChange={(e) => setItemDlg((p) => ({ ...p, draft: { ...p.draft, equation: e.target.value } }))}
                  fullWidth
                />
                <MathJaxContext config={mathjaxConfig}>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Preview
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <MathJax>{"\\(" + (itemDlg.draft.equation || "") + "\\)"}</MathJax>
                    </Box>
                  </Paper>
                </MathJaxContext>
              </>
            ) : null}

            {itemDlg.draft.type === "function" ? (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Language"
                    value={itemDlg.draft.language}
                    onChange={(e) => setItemDlg((p) => ({ ...p, draft: { ...p.draft, language: e.target.value } }))}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Code"
                  value={itemDlg.draft.code}
                  onChange={(e) => setItemDlg((p) => ({ ...p, draft: { ...p.draft, code: e.target.value } }))}
                  fullWidth
                  multiline
                  minRows={6}
                />
              </>
            ) : null}

            <Divider />

            {/* Item variables (unchanged) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Item variables</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  setItemDlg((p) => ({ ...p, draft: { ...p.draft, variables: [...(p.draft.variables || []), emptyVar()] } }))
                }
              >
                Add
              </Button>
            </Stack>

            {(itemDlg.draft.variables || []).length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No item variables.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {(itemDlg.draft.variables || []).map((v, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <TextField
                          label="Name"
                          value={v.name || ""}
                          onChange={(e) =>
                            setItemDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), name: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          label="Code"
                          value={v.code || ""}
                          onChange={(e) =>
                            setItemDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), code: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          label="Unit"
                          value={v.unit || ""}
                          onChange={(e) =>
                            setItemDlg((p) => {
                              const vars = [...(p.draft.variables || [])];
                              vars[idx] = { ...(vars[idx] || emptyVar()), unit: e.target.value };
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                          fullWidth
                        />
                      </Stack>

                      <TextField
                        label="Description"
                        value={v.description || ""}
                        onChange={(e) =>
                          setItemDlg((p) => {
                            const vars = [...(p.draft.variables || [])];
                            vars[idx] = { ...(vars[idx] || emptyVar()), description: e.target.value };
                            return { ...p, draft: { ...p.draft, variables: vars } };
                          })
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />

                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          color="error"
                          onClick={() =>
                            setItemDlg((p) => {
                              const vars = (p.draft.variables || []).filter((_, i) => i !== idx);
                              return { ...p, draft: { ...p.draft, variables: vars } };
                            })
                          }
                        >
                          Remove
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDlg({ open: false, sectionIdx: -1, idx: -1, draft: emptyItem() })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveItemDlg}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
